import type { Control, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/shared/shadcn/ui/form';

export interface FieldConfig {
  visible?: boolean;
  required?: boolean;
  fieldId?: string;
}

interface ConfigurableInputFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  config: FieldConfig | undefined;
  placeholder: string;
  label?: string;
  type?: 'text' | 'email' | 'number';
  customRules?: RegisterOptions<TFieldValues>;
  className?: string;
}

export function ConfigurableInputField<TFieldValues extends FieldValues>({
  control,
  name,
  config,
  placeholder,
  label,
  type = 'text',
  customRules = {},
  className = 'w-full rounded-lg border px-3 py-2 text-gray-700 focus:border-blue-500 focus:outline-none',
}: ConfigurableInputFieldProps<TFieldValues>) {
  if (!config?.visible) {
    return null;
  }

  const rules: RegisterOptions<TFieldValues> = {
    ...customRules,
  };

  // Add required rule if specified in config
  if (config.required && !customRules.required) {
    rules.required = `${placeholder} is required`;
  }

  const displayPlaceholder = `${placeholder}${config.required ? ' *' : ''}`;

  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <FormItem>
          {label && (
            <FormLabel>
              {label}
              {config.required ? ' *' : ''}
            </FormLabel>
          )}
          <FormControl>
            <input
              type={type}
              className={className}
              placeholder={displayPlaceholder}
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
